import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { authService } from "@/common/services/AuthService";
import { AppRouter } from "@/common/router/router";
import { Paths, RouteInfoMap } from "@/common/enums/RouterPaths";
import { config } from "@/config";

const axiosConfig: AxiosRequestConfig = {
  baseURL: config.backend.url,
  timeout: 50000,
};

export enum RequestHeaders {
  X_API_KEY = "X-API-Key",
  CONTENT_TYPE = "Content-Type",
  AUTHORIZATION = "Authorization",
}

type ErrorListener = (error: AxiosError) => void;

class HttpService {
  private readonly instance: AxiosInstance;
  private readonly errorListeners: ErrorListener[];

  constructor() {
    this.instance = axios.create(axiosConfig);
    this.errorListeners = [];
    this.setupMiddleware();
  }

  private setupMiddleware(): void {
    const badRequest = 400;
    const unAuthorized = 401;
    this.getInstance().interceptors.response.use(
      (response) => response,
      async (error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status ?? -1;
          if (status === unAuthorized) {
            authService.clearState();
            await authService.logout();
            await AppRouter.push({
              name: RouteInfoMap.get(Paths.Dashboard)?.name,
            });
          } else {
            if (status >= badRequest) {
              this.errorListeners.forEach((listener) => listener(error));
            }
          }
        }
      }
    );
  }

  getInstance(): AxiosInstance {
    return this.instance;
  }

  setAuthorizationHeader(token: string): void {
    this.instance.defaults.headers.common[RequestHeaders.AUTHORIZATION] = `Bearer ${token}`;
    this.instance.defaults.headers.common[RequestHeaders.X_API_KEY] = config.backend.ApiKey;
  }

  removeAuthorizationHeader(): void {
    delete this.instance.defaults.headers.common.Authorization;
  }

  addErrorListener(errorListener: ErrorListener): void {
    this.errorListeners.push(errorListener);
  }
}

export const httpService = new HttpService();
